/**
 * ProposalReview - section-agnostic review surface for an IntelligenceRun
 * awaiting review on a given target: the most recent one by default, or the
 * exact run named by `pinnedRunId` (see its own doc comment below) when the
 * host must not let the panel drift onto a different awaiting-review run for
 * the same target. It is mounted through a host EntityForm's function-form
 * `extraContent` (see EntityForm.tsx), which hands it live access to the
 * surrounding draft form via `onApplyToDraft`.
 *
 * This component NEVER calls a domain mutation client. Accepted proposals are
 * copied into the local form draft only; the section's ordinary Save remains
 * the only write to program data. MarkRunAppliedToDraft (fired from
 * `applyToDraft` below) is an audit transition recording that the copy
 * happened, and it must stay that way. Adding any deliveryClient/other domain
 * write here would recreate the second, divergent write path this design
 * exists to prevent.
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { NullValue, Value } from '@bufbuild/protobuf';
import { IntelligenceTypes } from '@mono/proto-clients';
import { Popover, PopoverContent, PopoverTrigger, SpecraBadge, SpecraButton, SpecraInlineBanner, SpecraInput } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQueryClient } from '@tanstack/react-query';
import { FileSearch, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { awaitingReviewQueryKey, failedRunQueryKey, isRunSettled, useAwaitingReviewRuns, useBulkReviewFieldProposals, useInFlightRuns, useIntelligenceRunProgress, useMarkRunAppliedToDraft, useReviewFieldProposal } from '../hooks/intelligenceHooks';
// Single place that reads Value.kind so casts don't scatter across the
// component - every current/proposed/reviewed value crosses the wire as a
// google.protobuf.Value. A listValue (e.g. SUBMITTED_TO_EMAILS) unwraps to an
// array of its members' own scalars - a struct/nested-list member has no
// scalar to give a form field, so it's dropped rather than surfacing
// undefined/[object Object] into the array.
//
// structValue is a deliberate case, not an omission: a multi-field proposal
// (e.g. touchpoint action items, which bundle description/ownerName/dueDate
// under one destination - see touchpoint_extractor.go's buildDocumentResult)
// carries its payload as a google.protobuf.Struct. toJson() is the same
// unwrap Struct.fields would otherwise require field-by-field.
function unwrapValue(v) {
    switch(v?.kind.case){
        case 'stringValue':
        case 'numberValue':
        case 'boolValue':
            return v.kind.value;
        case 'listValue':
            return v.kind.value.values.map((member)=>unwrapValue(member)).filter((m)=>m !== undefined && typeof m !== 'object');
        case 'structValue':
            return v.kind.value.toJson();
        default:
            return undefined;
    }
}
// A nullValue kind is a deliberate extractor finding - the source document
// said this field does not apply (e.g. a CPARS rating explicitly marked
// "N/A") - not the absence of one. `--` already means "no value here", so a
// null must read as something a reviewer can tell apart from that, without
// losing the fact that it's still a proposal worth reviewing.
function isExplicitNotApplicable(v) {
    return v?.kind.case === 'nullValue';
}
// A struct proposal (e.g. a touchpoint action item's description/ownerName/
// dueDate) has no single-field text a hand-edit could losslessly write back
// to - editableValue's flattened "key: value, ..." string is for display
// only, and re-parsing it would require this component to know the field
// names of every struct shape it might ever carry. Hand-editing is therefore
// not offered for one; reviseValue below also refuses to fabricate a
// stringValue for a struct template as a second line of defense.
function isStructProposal(v) {
    return v?.kind.case === 'structValue';
}
// A struct proposal has no single scalar to print - render every non-empty
// field as "key: value" so a reviewer sees the whole record (e.g. a
// touchpoint action item's description/ownerName/dueDate) without this
// component needing to know any field's name.
function displayStruct(obj) {
    return Object.entries(obj).filter(([, val])=>val !== undefined && val !== null && val !== '').map(([k, val])=>`${k}: ${val}`).join(', ') || '--';
}
// `format` is applied only to the scalar case (string/number/boolean) - a
// binding's formatter has no defined meaning for "no value", an explicit N/A,
// a list, or a struct, so each of those keeps its own dedicated rendering
// regardless of whether a formatter was supplied.
function displayValue(v, format) {
    if (isExplicitNotApplicable(v)) return 'Not applicable (source)';
    const unwrapped = unwrapValue(v);
    if (unwrapped === undefined) return '--';
    if (Array.isArray(unwrapped)) return unwrapped.length > 0 ? unwrapped.join(', ') : '--';
    if (typeof unwrapped === 'object') return displayStruct(unwrapped);
    return format ? format(unwrapped) : String(unwrapped);
}
// Seed text for the edit input. Deliberately NOT displayValue: the edit box
// needs a value the user can either accept unchanged or type over, and
// "Not applicable (source)" is not itself a valid re-entry for any field
// kind. An unwrapped null/absent value seeds blank, so re-saving an
// untouched edit reproduces the same "no value" outcome reviseValue gives a
// blank edit below.
function editableValue(v) {
    const unwrapped = unwrapValue(v);
    if (unwrapped === undefined) return '';
    if (Array.isArray(unwrapped)) return String(unwrapped);
    return typeof unwrapped === 'object' ? displayStruct(unwrapped) : String(unwrapped);
}
// Rebuilds a Value of the same kind the template (the proposal's own
// proposed_value) carried, so a hand-edited string round-trips as a
// number/boolean when that's what the field actually is. Returns null when
// raw cannot be parsed as the template's kind (e.g. non-numeric text for a
// numberValue field) so the caller rejects the edit instead of silently
// substituting a value - a valid but unintended value like 0 - the user
// never typed. CPARS score fields accept zero, so that substitution would
// otherwise record a plausible-looking value for what was really a typo.
//
// A nullValue template (an explicit N/A) only ever comes from a rating field
// the extractor resolved to "no number" (see cpars_extractor.go's
// ratingProposal - it's the only producer of a null proposed_value, and it's
// always a numeric destination). So it's handled the same way a numberValue
// template is: blank keeps the field unrated (returns a fresh null Value,
// not a rejection), a numeric entry resolves the rating to a real score, and
// anything else is rejected rather than recorded as if it were the rating.
function reviseValue(raw, template) {
    // Belt-and-suspenders: the Edit control is disabled for a struct proposal
    // (see isStructProposal), but this function must never turn one into a
    // stringValue even if some future caller reaches it directly.
    if (template?.kind.case === 'structValue') return null;
    if (template?.kind.case === 'numberValue') {
        const trimmed = raw.trim();
        if (trimmed === '' || Number.isNaN(Number(trimmed))) return null;
        return new Value({
            kind: {
                case: 'numberValue',
                value: Number(trimmed)
            }
        });
    }
    if (template?.kind.case === 'boolValue') {
        return new Value({
            kind: {
                case: 'boolValue',
                value: raw === 'true'
            }
        });
    }
    if (isExplicitNotApplicable(template)) {
        const trimmed = raw.trim();
        if (trimmed === '') return new Value({
            kind: {
                case: 'nullValue',
                value: NullValue.NULL_VALUE
            }
        });
        if (Number.isNaN(Number(trimmed))) return null;
        return new Value({
            kind: {
                case: 'numberValue',
                value: Number(trimmed)
            }
        });
    }
    return new Value({
        kind: {
            case: 'stringValue',
            value: raw
        }
    });
}
// Mirrors the block ids emailsource.go's buildEmailBlocks emits for header
// fields (email-header-date, -from, -to, -cc, -subject) - keep the two in
// agreement if that scheme changes.
const EMAIL_HEADER_FIELD_LABELS = {
    'email-header-date': 'Date',
    'email-header-from': 'From',
    'email-header-to': 'To',
    'email-header-cc': 'Cc',
    'email-header-subject': 'Subject'
};
// An EMAIL_HEADER location never carries a page/index (see LocationForBlock
// in emailsource.go), so this always resolves off the block id - an
// unrecognised or absent one still needs a non-empty label, since undefined
// here means the Location row disappears entirely.
function emailHeaderLocationLabel(blockId) {
    const field = EMAIL_HEADER_FIELD_LABELS[blockId];
    return field ? `email header · ${field}` : 'email header';
}
// Mirrors emailsource.go's body paragraph block ids (email-body-0001, ...).
const EMAIL_BODY_BLOCK_RE = /^email-body-(\d+)$/;
// A TEXT location always carries a block_id or source_span (provenance.go's
// validateLocation enforces it), but never both formatted the same way - a
// raw source_span is an internal match string, not something to show a
// reviewer verbatim.
function textLocationLabel(loc) {
    if (loc.blockId) {
        const bodyMatch = EMAIL_BODY_BLOCK_RE.exec(loc.blockId);
        return bodyMatch ? `email body ¶${Number(bodyMatch[1])}` : 'text block';
    }
    return loc.sourceSpan ? 'text span' : undefined;
}
function locationLabel(loc) {
    if (!loc) return undefined;
    switch(loc.kind){
        case IntelligenceTypes.SourceLocationKind.PAGE:
            return loc.index > 0 ? `p.${loc.index}` : undefined;
        case IntelligenceTypes.SourceLocationKind.SLIDE:
            return loc.index > 0 ? `slide ${loc.index}` : undefined;
        case IntelligenceTypes.SourceLocationKind.SHEET:
            return loc.sheetName || (loc.index > 0 ? `sheet ${loc.index}` : undefined);
        case IntelligenceTypes.SourceLocationKind.EMAIL_HEADER:
            return emailHeaderLocationLabel(loc.blockId);
        case IntelligenceTypes.SourceLocationKind.TEXT:
            return textLocationLabel(loc);
        default:
            return undefined;
    }
}
const RUN_STATE_LABEL = {
    [IntelligenceTypes.IntelligenceRunState.READY_FOR_REVIEW]: 'Ready for review',
    [IntelligenceTypes.IntelligenceRunState.PARTIALLY_READY]: 'Partially ready'
};
const ACCEPTED_STATES = [
    IntelligenceTypes.ProposalReviewState.ACCEPTED,
    IntelligenceTypes.ProposalReviewState.USER_EDITED
];
// A row the reviewer has not yet touched. bulkAccept below only ever
// decides one of these - see its own comment for why a decided row (in
// either direction) must never be re-sent.
const UNDECIDED_STATES = [
    IntelligenceTypes.ProposalReviewState.PENDING,
    IntelligenceTypes.ProposalReviewState.UNSPECIFIED
];
// Provenance popover for one ProposalSource. Deliberately separate from the
// shared ReviewSourceChip: a source here also carries filename/document_type
// and a location kind ReviewSourceChip's quote+confidence+page shape doesn't
// model, but it keeps the same trigger/popover visual language.
function ProposalSourceProvenance({ source }) {
    const confidencePct = `${Math.round(source.confidence * 100)}%`;
    const location = locationLabel(source.location);
    return /*#__PURE__*/ _jsxs(Popover, {
        children: [
            /*#__PURE__*/ _jsx(PopoverTrigger, {
                asChild: true,
                children: /*#__PURE__*/ _jsx("button", {
                    type: "button",
                    className: "inline-flex items-center text-muted-foreground hover:text-foreground",
                    "aria-label": `Source for ${source.filename || 'proposal'}`,
                    title: "View source",
                    "data-testid": Testids.proposalReview.provenanceTrigger,
                    children: /*#__PURE__*/ _jsx(FileSearch, {
                        className: "h-3 w-3",
                        "aria-hidden": true
                    })
                })
            }),
            /*#__PURE__*/ _jsxs(PopoverContent, {
                className: "w-80 text-sm",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mb-2 flex items-center justify-between",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-medium",
                                children: "Source"
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: "text-xs text-muted-foreground",
                                children: [
                                    "confidence ",
                                    confidencePct
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("dl", {
                        className: "space-y-1",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex justify-between gap-3",
                                children: [
                                    /*#__PURE__*/ _jsx("dt", {
                                        className: "text-muted-foreground",
                                        children: "Document"
                                    }),
                                    /*#__PURE__*/ _jsx("dd", {
                                        className: "truncate text-right",
                                        children: source.filename || 'Unknown document'
                                    })
                                ]
                            }),
                            location && /*#__PURE__*/ _jsxs("div", {
                                className: "flex justify-between gap-3",
                                children: [
                                    /*#__PURE__*/ _jsx("dt", {
                                        className: "text-muted-foreground",
                                        children: "Location"
                                    }),
                                    /*#__PURE__*/ _jsx("dd", {
                                        className: "truncate text-right",
                                        children: location
                                    })
                                ]
                            })
                        ]
                    }),
                    source.excerpt && /*#__PURE__*/ _jsxs("blockquote", {
                        className: "mt-2 border-l-2 border-muted pl-2 text-xs italic text-muted-foreground",
                        "data-testid": Testids.proposalReview.provenanceExcerpt,
                        children: [
                            "“",
                            source.excerpt,
                            "”"
                        ]
                    })
                ]
            })
        ]
    });
}
export function ProposalReview({ target, workflowKind, draftId, pinnedRunId, fieldBindings, onApplyToDraft }) {
    const { data: runs } = useAwaitingReviewRuns(workflowKind, target);
    const { data: inFlightRuns } = useInFlightRuns(workflowKind, target);
    // Watches the exact run pinnedRunId names, by id, independent of whichever
    // list queries above happen to have caught it in. See `processing` and the
    // settle-edge effect below for why this exists: useInFlightRuns/
    // useAwaitingReviewRuns are both polled list snapshots, and a run that
    // transitions QUEUED -> READY_FOR_REVIEW between two polls (measured ~50ms
    // for a proof-of-delivery run, which does no LLM call) can cross both lists
    // without ever being caught IN either one - it was never observed in-flight,
    // so there is no falling edge to invalidate on (SAD-392). Disabled (and
    // fetches nothing) while pinnedRunId is undefined or '' ("not pinned yet" -
    // see pinnedRunId's own doc comment).
    const { data: pinnedRunProgress } = useIntelligenceRunProgress(pinnedRunId ?? '', {
        enabled: !!pinnedRunId
    });
    const qc = useQueryClient();
    // pinnedRunId !== undefined (not truthiness) distinguishes "caller passed a
    // pin, even ''" from "caller never passed the prop" - only the latter falls
    // back to runs?.[0]. A pinned run resolves ONLY to itself, never falling
    // back: once a host has pinned to a specific (e.g. just-started
    // re-extraction) run, showing a different, older run's proposals in its
    // place would be exactly the stale-data regression this prop exists to
    // prevent. Until the pinned run reaches an awaiting-review state, the panel
    // renders nothing (see the !run check below) rather than a misleading
    // substitute. See pinnedRunId's own doc comment on ProposalReviewProps.
    const liveRun = pinnedRunId !== undefined ? runs?.find((r)=>r.id === pinnedRunId) : runs?.[0];
    const reviewProposal = useReviewFieldProposal();
    const bulkReview = useBulkReviewFieldProposals();
    const markApplied = useMarkRunAppliedToDraft(workflowKind, target);
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [editError, setEditError] = useState(null);
    const [skipNote, setSkipNote] = useState(null);
    // Which run's apply actually succeeded, by id - not a bare boolean, so a
    // NEW awaiting-review run for the same target (the reviewer processing a
    // second proof inside one still-open drawer, e.g. DeliverableScheduleTab's
    // unpinned mount) can re-enable Apply instead of finding it permanently
    // gone for the lifetime of the mount. Set only from markApplied's onSuccess
    // below - see applyToDraft for why that has to stay a success callback.
    const [appliedRunId, setAppliedRunId] = useState(null);
    // Post-apply copy of the run. MarkRunAppliedToDraft moves the run out of the
    // awaiting-review set server-side and now invalidates that query, so the live
    // run goes undefined the moment apply succeeds. Holding the last-seen run
    // here keeps the applied summary (and its evidence) on screen for THIS
    // draft, while the cache no longer carries it into the next one - the leak
    // matt reported when a fresh "New CPARS Score" opened the previous draft's
    // review block. A fresh mount starts with no snapshot, which is the point.
    const [appliedSnapshot, setAppliedSnapshot] = useState(null);
    // Whether the reviewed rows are expanded after apply. Collapsed by default:
    // the evidence stays reachable, it just stops being the whole panel once the
    // reviewer is done with it.
    const [showEvidence, setShowEvidence] = useState(false);
    // A NEW run for this target has arrived while an old one is still snapshotted
    // - the id, not identity/reference equality, is what makes this "new" rather
    // than the same run refetched. When that happens the live run wins over the
    // stale snapshot, same convention DeliverableScheduleTab's podResolutionUnrecorded
    // uses for run supersession. Derived, not an effect: between clicking Apply on
    // this new run and its audit succeeding, appliedSnapshot is already the new run
    // while appliedRunId is still the old one, so `applied` below is correctly
    // false for that whole window - the applied summary doesn't flash early and
    // the audit-retry banner still works. Once a run IS applied, `applied` stays
    // true for that same run (appliedRunId === run.id) so it can't be applied
    // twice, but flips back to false the moment a run with a different id shows
    // up, re-enabling Apply for it.
    const superseded = appliedSnapshot !== null && liveRun !== undefined && liveRun.id !== appliedSnapshot.id;
    const run = superseded ? liveRun : appliedSnapshot ?? liveRun;
    // A run for this target is still working. Between StartIntelligenceRun
    // returning and its proposals being ready, this is the ONLY thing the panel
    // can honestly say - without it the panel renders nothing at all and the
    // user learns to click Re-extract again to find out whether it finished
    // (which also spawns a duplicate run). See useInFlightRuns.
    //
    // A PINNED panel only counts its OWN run as processing. Reporting some other
    // run's progress there would be the same category of substitution pinnedRunId
    // exists to forbid.
    //
    // A pinned panel prefers the by-id progress observation over the in-flight
    // list: once pinnedRunProgress has actually observed the run (data !==
    // undefined - the query is disabled/unfetched otherwise), its own state is
    // the ground truth, settled or not. Before that first observation lands -
    // the query is disabled, still in flight, or the run predates this mount -
    // fall back to today's in-flight-list membership check, which is still
    // correct for a run this panel hasn't watched settle yet.
    const processing = pinnedRunId !== undefined ? pinnedRunProgress !== undefined ? !isRunSettled(pinnedRunProgress.state) : inFlightRuns?.some((r)=>r.id === pinnedRunId) ?? false : (inFlightRuns?.length ?? 0) > 0;
    const wasProcessing = useRef(false);
    const { programId, entityType, entityId } = target;
    useEffect(()=>{
        // Falling edge only: the run that was working has left the in-flight set,
        // so the awaiting-review list is now stale and its refetch is what makes
        // the proposals appear on their own - AND the failed-run query is now
        // stale too, since a run that just failed must not keep serving a
        // failed-run query's cached "not failed yet" answer with nothing left to
        // ever refetch it (SAD-321, SAD-154: an unpinned host with no other
        // refetch trigger went completely silent this way). Scoped to these two
        // keys, not the whole INTELLIGENCE_KEY tree - a blanket invalidate would
        // also reset the in-flight poll above.
        //
        // Still correct to keep for the pinned case (harmless, not load-bearing
        // there): a pinned run that DOES get caught in-flight still produces a
        // falling edge here. The state-based effect below is what covers the run
        // this one can't see - one that never appears in-flight at all.
        if (wasProcessing.current && !processing) {
            void qc.invalidateQueries({
                queryKey: awaitingReviewQueryKey(workflowKind, {
                    programId,
                    entityType,
                    entityId
                })
            });
            void qc.invalidateQueries({
                queryKey: failedRunQueryKey(workflowKind, {
                    programId,
                    entityType,
                    entityId
                })
            });
        }
        wasProcessing.current = processing;
    }, [
        processing,
        qc,
        workflowKind,
        programId,
        entityType,
        entityId
    ]);
    // Which pinned run id this effect has already invalidated for, so a settled
    // run sitting on screen across re-renders (or a second observation of the
    // same settled state) doesn't re-invalidate on every render.
    const notifiedSettledPinnedRunId = useRef(null);
    useEffect(()=>{
        // State-based settle detection, independent of the in-flight list: this is
        // the SAD-392 fix. A proof-of-delivery run measured on the live stack
        // moves QUEUED -> READY_FOR_REVIEW in ~50ms - no LLM call sits in the
        // pipeline - so useInFlightRuns' 2s poll can catch the run in neither the
        // in-flight list nor a "was in-flight, now isn't" transition: by the time
        // any poll fires, the run may already have gone straight from not-yet-
        // observed to settled. The falling-edge effect above has nothing to
        // trigger on in that case, and the panel stayed empty until the host's
        // Re-extract was clicked a second time (matt, SAD-392). Watching THIS run
        // by id sidesteps polling cadence entirely: the moment any observation of
        // it - including the very first - reports a settled state, this fires,
        // with no requirement that a prior "processing" observation ever landed.
        if (pinnedRunId && pinnedRunProgress !== undefined && isRunSettled(pinnedRunProgress.state) && notifiedSettledPinnedRunId.current !== pinnedRunId) {
            void qc.invalidateQueries({
                queryKey: awaitingReviewQueryKey(workflowKind, {
                    programId,
                    entityType,
                    entityId
                })
            });
            void qc.invalidateQueries({
                queryKey: failedRunQueryKey(workflowKind, {
                    programId,
                    entityType,
                    entityId
                })
            });
            notifiedSettledPinnedRunId.current = pinnedRunId;
        }
    }, [
        pinnedRunId,
        pinnedRunProgress,
        qc,
        workflowKind,
        programId,
        entityType,
        entityId
    ]);
    // Rendering nothing without an awaiting run is the contract this component
    // exists to hold: the host form must look and behave exactly as it does
    // today when there's no run in play, so existing journeys don't regress.
    if (!run) {
        if (!processing) return null;
        return /*#__PURE__*/ _jsxs("div", {
            className: "flex items-center gap-2 rounded-lg border border-border p-4 text-sm text-muted-foreground",
            "data-testid": Testids.proposalReview.processing,
            children: [
                /*#__PURE__*/ _jsx(Loader2, {
                    className: "h-4 w-4 animate-spin",
                    "aria-hidden": true
                }),
                /*#__PURE__*/ _jsx("span", {
                    children: "Extracting fields from the document - proposals will appear here when ready."
                })
            ]
        });
    }
    const applied = run !== undefined && appliedRunId !== null && appliedRunId === run.id;
    const proposals = run.proposals;
    const startEdit = (p)=>{
        setEditingId(p.id);
        setEditValue(editableValue(p.proposedValue));
        setEditError(null);
    };
    const cancelEdit = ()=>{
        setEditingId(null);
        setEditError(null);
    };
    const saveEdit = (p)=>{
        const revised = reviseValue(editValue, p.proposedValue);
        if (revised === null) {
            // Keep the edit open and reject the input rather than silently
            // recording a value (like 0) the user never typed.
            setEditError('Enter a valid number.');
            return;
        }
        reviewProposal.mutate({
            runId: run.id,
            proposalId: p.id,
            reviewState: IntelligenceTypes.ProposalReviewState.USER_EDITED,
            reviewedValue: revised
        });
        setEditingId(null);
        setEditError(null);
    };
    const accept = (p)=>{
        reviewProposal.mutate({
            runId: run.id,
            proposalId: p.id,
            reviewState: IntelligenceTypes.ProposalReviewState.ACCEPTED
        });
    };
    const reject = (p)=>{
        reviewProposal.mutate({
            runId: run.id,
            proposalId: p.id,
            reviewState: IntelligenceTypes.ProposalReviewState.REJECTED
        });
    };
    // Safe bulk accept: a conflict flagged NEEDS_USER_REVIEW or any warning
    // means the extractor itself isn't confident, so blindly accepting it here
    // would be the exact blind-approval failure this control exists to
    // prevent. A row the reviewer has already decided (anything outside
    // UNDECIDED_STATES) is withheld for a different reason: re-sending
    // ACCEPTED for it isn't "safe", it's wrong - an already-REJECTED proposal
    // would flip back to accepted, and for an already-USER_EDITED one the
    // reviewer's corrected value would be discarded outright, since
    // applyToDraft only reads reviewedValue while reviewState stays
    // USER_EDITED (see below) - overwriting that state to ACCEPTED loses the
    // correction. An already-ACCEPTED row is excluded too, but only because
    // it needs no re-decision, not because it's unsafe.
    const bulkAccept = ()=>{
        const isUndecided = (p)=>UNDECIDED_STATES.includes(p.reviewState);
        const eligible = proposals.filter((p)=>isUndecided(p) && p.conflictState !== IntelligenceTypes.ProposalConflictState.NEEDS_USER_REVIEW && (p.warnings.length === 0 || isExplicitNotApplicable(p.proposedValue)));
        const alreadyDecided = proposals.filter((p)=>!isUndecided(p));
        const needsManualReview = proposals.filter((p)=>isUndecided(p) && (p.conflictState === IntelligenceTypes.ProposalConflictState.NEEDS_USER_REVIEW || p.warnings.length > 0 && !isExplicitNotApplicable(p.proposedValue)));
        // These are two different reasons a row doesn't get bulk-accepted, and
        // conflating them into one "needs manual review" note (the old wording)
        // was untrue for a row the reviewer had already decided themselves - so
        // each is only mentioned when it actually applies.
        const notes = [];
        if (alreadyDecided.length > 0) {
            const n = alreadyDecided.length;
            notes.push(`${n} proposal${n === 1 ? '' : 's'} already decided by you ${n === 1 ? 'was' : 'were'} left as-is.`);
        }
        if (needsManualReview.length > 0) {
            const n = needsManualReview.length;
            notes.push(`Skipped ${n} proposal${n === 1 ? '' : 's'} that ${n === 1 ? 'needs' : 'need'} manual review (a conflict or a warning). Accept ${n === 1 ? 'it' : 'them'} individually.`);
        }
        setSkipNote(notes.length > 0 ? notes.join(' ') : null);
        if (eligible.length === 0) return;
        bulkReview.mutate({
            runId: run.id,
            decisions: eligible.map((p)=>({
                    proposalId: p.id,
                    reviewState: IntelligenceTypes.ProposalReviewState.ACCEPTED
                }))
        });
    };
    // bulkAccept's eligibility filter reads the `proposals` snapshot from the
    // query cache, which only advances on a mutation's onSuccess invalidation -
    // so a Reject/Save-edit still in flight leaves that row looking undecided
    // to a bulk accept fired in the same window. Disabling every review control
    // while any review mutation is pending closes that race instead of letting
    // a second write reach the server before the first one has landed.
    // markApplied is included too: `applied` stays false until it succeeds, so
    // the controls below would otherwise stay live for that whole window - a
    // Reject or Edit landing there changes the server run after the draft
    // already received the pre-change accepted values, and the run/draft
    // silently disagree the moment `applied` flips true and hides them.
    const reviewMutationPending = reviewProposal.isPending || bulkReview.isPending || markApplied.isPending;
    const acceptedProposals = proposals.filter((p)=>ACCEPTED_STATES.includes(p.reviewState));
    const applyToDraft = ()=>{
        const values = acceptedProposals.map((p)=>{
            const binding = fieldBindings[p.destination?.fieldName ?? IntelligenceTypes.DestinationFieldName.UNSPECIFIED];
            if (!binding) return null;
            const raw = unwrapValue(p.reviewState === IntelligenceTypes.ProposalReviewState.USER_EDITED ? p.reviewedValue : p.proposedValue);
            // A null/absent value (an explicit N/A, or simply no value) must
            // reach the draft as undefined, never through `parse` - every
            // numeric binding here implements parse as `Number(v)`, and
            // Number(undefined) is NaN, not "leave this field empty". Skipping
            // parse for undefined fixes that once for every binding, current
            // and future, instead of requiring each parse function to
            // special-case undefined itself.
            return {
                formField: binding.formField,
                value: raw === undefined ? undefined : binding.parse ? binding.parse(raw) : raw,
                // Only an accepted proposal whose value the document explicitly
                // rated N/A carries this; a USER_EDITED one reflects what the
                // reviewer typed instead, so it is judged on its own value.
                notApplicable: isExplicitNotApplicable(p.reviewState === IntelligenceTypes.ProposalReviewState.USER_EDITED ? p.reviewedValue : p.proposedValue)
            };
        }).filter((v)=>v !== null);
        onApplyToDraft(values);
        // Snapshot BEFORE the audit call so the panel never flashes empty between
        // the invalidation its success triggers and this state landing.
        setAppliedSnapshot(run);
        // Run A's "skipped N proposals" note must not carry onto run B's panel
        // once B supersedes A - it describes A's bulk-accept decisions, not B's.
        setSkipNote(null);
        // applied is set only from the mutation's success callback: if the audit
        // call fails (network/backend error), the success notice must not show
        // and the Apply button must not be permanently disabled -- otherwise the
        // user could save the populated domain form while the run is never
        // marked applied, with no way to retry the audit transition.
        markApplied.mutate({
            runId: run.id,
            draftId,
            proposalIds: acceptedProposals.map((p)=>p.id)
        }, {
            onSuccess: ()=>setAppliedRunId(run.id)
        });
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3 rounded-lg border border-border p-4",
        "data-testid": Testids.proposalReview.panel,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between gap-3",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-sm font-medium",
                        "data-testid": Testids.proposalReview.runState,
                        children: RUN_STATE_LABEL[run.state] ?? 'Awaiting review'
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: "text-xs text-muted-foreground",
                        children: [
                            run.progressPercent,
                            "%"
                        ]
                    })
                ]
            }),
            run.state === IntelligenceTypes.IntelligenceRunState.PARTIALLY_READY && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                        tone: "warning",
                        "data-testid": Testids.proposalReview.partialNote,
                        children: "Extraction completed with items that need review."
                    }),
                    run.warnings.length > 0 && /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-0.5 text-xs text-warning",
                        "data-testid": Testids.proposalReview.runWarnings,
                        children: run.warnings.map((w)=>/*#__PURE__*/ _jsx("li", {
                                children: w
                            }, w))
                    })
                ]
            }),
            applied && /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center justify-between gap-2",
                "data-testid": Testids.proposalReview.appliedSummary,
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: "text-sm text-muted-foreground",
                        children: [
                            acceptedProposals.length,
                            " reviewed",
                            ' ',
                            acceptedProposals.length === 1 ? 'field' : 'fields',
                            " applied to this form from",
                            ' ',
                            run.proposals.length,
                            " proposal",
                            run.proposals.length === 1 ? '' : 's',
                            "."
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "button",
                        size: "sm",
                        variant: "ghost",
                        onClick: ()=>setShowEvidence((v)=>!v),
                        "data-testid": Testids.proposalReview.evidenceToggle,
                        children: showEvidence ? 'Hide evidence' : 'Show evidence'
                    })
                ]
            }),
            proposals.length === 0 ? /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                "data-testid": Testids.proposalReview.noProposalsNote,
                children: "No fields could be proposed from this document. Enter the values for this record by hand."
            }) : !applied || showEvidence ? /*#__PURE__*/ _jsx("div", {
                className: "divide-y divide-border/60",
                children: proposals.map((p)=>{
                    const binding = fieldBindings[p.destination?.fieldName ?? IntelligenceTypes.DestinationFieldName.UNSPECIFIED];
                    const isEditing = editingId === p.id;
                    const isConflict = p.conflictState === IntelligenceTypes.ProposalConflictState.NEEDS_USER_REVIEW;
                    return /*#__PURE__*/ _jsx("div", {
                        className: "py-3 first:pt-0",
                        "data-testid": Testids.proposalReview.proposalRow,
                        "data-review-state": IntelligenceTypes.ProposalReviewState[p.reviewState],
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-start justify-between gap-3",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "min-w-0 flex-1",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center gap-1.5 text-sm font-medium",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    children: binding?.label ?? p.targetFieldPath
                                                }),
                                                p.sources.map((s)=>/*#__PURE__*/ _jsx(ProposalSourceProvenance, {
                                                        source: s
                                                    }, `${s.artifactId}-${s.excerpt}`)),
                                                isConflict && /*#__PURE__*/ _jsx(SpecraBadge, {
                                                    variant: "danger",
                                                    "data-testid": Testids.proposalReview.conflictMarker,
                                                    children: "Needs review"
                                                })
                                            ]
                                        }),
                                        !binding && /*#__PURE__*/ _jsx("p", {
                                            className: "text-xs text-muted-foreground",
                                            children: "Not applicable: no form field is bound to this proposal."
                                        }),
                                        isStructProposal(p.proposedValue) && /*#__PURE__*/ _jsx("p", {
                                            className: "text-xs text-muted-foreground",
                                            children: "Multi-field proposal — accept or reject; hand-editing isn't supported."
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "mt-1 flex flex-wrap items-center gap-2 text-sm",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "text-muted-foreground",
                                                    "data-testid": Testids.proposalReview.currentValue,
                                                    children: displayValue(p.currentValue, binding?.format)
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    "aria-hidden": true,
                                                    children: "→"
                                                }),
                                                isEditing ? /*#__PURE__*/ _jsx(SpecraInput, {
                                                    autoFocus: true,
                                                    value: editValue,
                                                    onChange: (e)=>setEditValue(e.target.value),
                                                    className: "h-7 w-40",
                                                    "data-testid": Testids.proposalReview.editInput
                                                }) : /*#__PURE__*/ _jsx("span", {
                                                    className: "font-medium text-foreground",
                                                    "data-testid": Testids.proposalReview.proposedValue,
                                                    children: displayValue(p.proposedValue, binding?.format)
                                                }),
                                                /*#__PURE__*/ _jsxs("span", {
                                                    className: "text-xs text-muted-foreground",
                                                    "data-testid": Testids.proposalReview.confidence,
                                                    children: [
                                                        Math.round(p.confidence * 100),
                                                        "%"
                                                    ]
                                                })
                                            ]
                                        }),
                                        p.reviewState === IntelligenceTypes.ProposalReviewState.USER_EDITED && !isEditing && /*#__PURE__*/ _jsxs("p", {
                                            className: "mt-1 text-xs text-muted-foreground",
                                            children: [
                                                "Edited to ",
                                                displayValue(p.reviewedValue, binding?.format)
                                            ]
                                        }),
                                        isEditing && editError && /*#__PURE__*/ _jsx("p", {
                                            className: "mt-1 text-xs text-danger",
                                            "data-testid": Testids.proposalReview.editError,
                                            children: editError
                                        }),
                                        p.warnings.length > 0 && /*#__PURE__*/ _jsx("ul", {
                                            className: "mt-1 space-y-0.5 text-xs text-warning",
                                            children: p.warnings.map((w)=>/*#__PURE__*/ _jsx("li", {
                                                    children: w
                                                }, w))
                                        })
                                    ]
                                }),
                                !applied && /*#__PURE__*/ _jsx("div", {
                                    className: "flex shrink-0 items-center gap-1",
                                    children: isEditing ? /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                type: "button",
                                                size: "sm",
                                                onClick: ()=>saveEdit(p),
                                                disabled: reviewMutationPending,
                                                "data-testid": Testids.proposalReview.editSave,
                                                children: "Save"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                type: "button",
                                                size: "sm",
                                                variant: "ghost",
                                                onClick: cancelEdit,
                                                children: "Cancel"
                                            })
                                        ]
                                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                type: "button",
                                                size: "sm",
                                                variant: p.reviewState === IntelligenceTypes.ProposalReviewState.ACCEPTED ? 'default' : 'secondary',
                                                onClick: ()=>accept(p),
                                                disabled: reviewMutationPending,
                                                "data-testid": Testids.proposalReview.accept,
                                                children: "Accept"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                type: "button",
                                                size: "sm",
                                                variant: p.reviewState === IntelligenceTypes.ProposalReviewState.REJECTED ? 'default' : 'destructive',
                                                onClick: ()=>reject(p),
                                                disabled: reviewMutationPending,
                                                "data-testid": Testids.proposalReview.reject,
                                                children: "Reject"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                type: "button",
                                                size: "sm",
                                                variant: "secondary",
                                                onClick: ()=>startEdit(p),
                                                disabled: isStructProposal(p.proposedValue) || reviewMutationPending,
                                                "data-testid": Testids.proposalReview.edit,
                                                children: "Edit"
                                            })
                                        ]
                                    })
                                })
                            ]
                        })
                    }, p.id);
                })
            }) : null,
            !applied && /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center justify-between gap-2 border-t border-border/60 pt-3",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                type: "button",
                                size: "sm",
                                variant: "secondary",
                                onClick: bulkAccept,
                                disabled: reviewMutationPending,
                                "data-testid": Testids.proposalReview.bulkAccept,
                                children: bulkReview.isPending ? 'Accepting…' : 'Accept all safe proposals'
                            }),
                            skipNote && /*#__PURE__*/ _jsx("p", {
                                className: "mt-1 text-xs text-muted-foreground",
                                "data-testid": Testids.proposalReview.bulkSkipNote,
                                children: skipNote
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "button",
                        size: "sm",
                        disabled: applied || acceptedProposals.length === 0 || markApplied.isPending || reviewMutationPending,
                        onClick: applyToDraft,
                        "data-testid": Testids.proposalReview.apply,
                        children: markApplied.isPending ? 'Applying…' : 'Apply to form'
                    })
                ]
            }),
            applied && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "info",
                "data-testid": Testids.proposalReview.appliedNotice,
                children: "These values are now in the form but have not been saved. Use the form's Save to record them."
            }),
            markApplied.isError && !applied && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "danger",
                "data-testid": Testids.proposalReview.applyError,
                children: "Could not record that these values were applied. The values are in the form, but the audit record was not saved - use Apply to form again to retry."
            })
        ]
    });
}
